import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { IMPORT_USER_STATE } from './graphql';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ImportPanel = () => {
  const [destinationUserId, setDestinationUserId] = useState('');
  const [exportJson, setExportJson] = useState('');
  const [filename, setFilename] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [runImport, { data, loading, error, reset }] = useMutation(IMPORT_USER_STATE);

  const destValid = UUID_RE.test(destinationUserId.trim());

  let parsedPreview: {
    sourceUserId?: string;
    sourceAppId?: string;
    schemaVersion?: number;
    exportedAt?: string;
  } | null = null;
  let parseError: string | null = null;
  if (exportJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(exportJson);
      parsedPreview = {
        sourceUserId: parsed.sourceUserId,
        sourceAppId: parsed.sourceAppId,
        schemaVersion: parsed.schemaVersion,
        exportedAt: parsed.exportedAt,
      };
    } catch {
      parseError = 'File content is not valid JSON.';
    }
  }

  const canSubmit = destValid && !!exportJson.trim() && !parseError;
  const result = data?.importUserState;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setExportJson(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsText(file);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    reset();
    runImport({
      variables: {
        input: {
          destinationUserId: destinationUserId.trim(),
          exportJson,
        },
      },
    });
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <span className="font-weight-bold">
          <i className="fas fa-upload mr-2" />
          Import User State
        </span>
      </Card.Header>
      <Card.Body>
        <Alert variant="warning" className="py-2">
          <i className="fas fa-exclamation-triangle mr-2" />
          <b>Destructive.</b> Overwrites the destination account.
        </Alert>

        <Form>
          <Form.Group>
            <Form.Label>Destination User ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. 11111111-2222-3333-4444-555555555555 (must be in userID allowlist)"
              className="dimmed-placeholder"
              value={destinationUserId}
              onChange={(e) => setDestinationUserId(e.currentTarget.value)}
              isInvalid={destinationUserId.length > 0 && !destValid}
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">Must be a valid UUID.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Export file (JSON)</Form.Label>
            <div style={{ maxWidth: 360 }}>
              <Form.File
                custom
                label={filename ?? 'Choose a .json file...'}
                accept="application/json,.json"
                onChange={handleFile}
                disabled={loading}
              />
            </div>
          </Form.Group>

          <Form.Group>
            <Form.Label>...or paste JSON directly</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={exportJson}
              onChange={(e) => {
                setExportJson(e.currentTarget.value);
                setFilename(null);
              }}
              placeholder='e.g. {"schemaVersion":2,"sourceUserId":"...","data":{...}}'
              className="dimmed-placeholder"
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              disabled={loading}
              isInvalid={!!parseError}
            />
            {parseError && <Form.Text className="text-danger">{parseError}</Form.Text>}
          </Form.Group>

          {parsedPreview && !parseError && (
            <Alert variant="light" className="py-2 border">
              <div><b>File preview</b></div>
              <div>Source app: <code>{parsedPreview.sourceAppId}</code></div>
              <div>Source user: <code>{parsedPreview.sourceUserId}</code></div>
              <div>
                Exported at:{' '}
                <code>{parsedPreview.exportedAt ? new Date(parsedPreview.exportedAt).toLocaleString() : ''}</code>
              </div>
            </Alert>
          )}

          <Button
            variant="primary"
            disabled={!canSubmit || loading}
            onClick={() => setShowConfirm(true)}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="mr-2" />
                Importing...
              </>
            ) : (
              'Import'
            )}
          </Button>
        </Form>

        {error && (
          <Alert variant="danger" className="mt-3 py-2">
            {error.message}
          </Alert>
        )}

        {result && !loading && !error && (
          <>
            <Alert variant="success" className="mt-4 py-2 mb-2">
              <i className="fas fa-check-circle mr-2" />
              <b>Import complete.</b> Destination account <code>{result.destinationUserId}</code> has been overwritten with the imported progress.
            </Alert>
            <Table bordered size="sm">
            <tbody>
              <tr>
                <th style={{ width: 280 }}>Source user (from file)</th>
                <td><code>{result.sourceUserId}</code></td>
              </tr>
              <tr>
                <th>Destination user</th>
                <td><code>{result.destinationUserId}</code></td>
              </tr>
              <tr>
                <th>AppUser applied?</th>
                <td>{result.appUserApplied ? '✅ Yes' : '❌ No'}</td>
              </tr>
              <tr>
                <th>MissionSetUser</th>
                <td>
                  applied <b>{result.missionSetUserRowsApplied}</b> · deleted{' '}
                  <b>{result.missionSetUserRowsDeleted}</b>
                </td>
              </tr>
              {(result.phaseTwoCounts ?? []).map(
                (entry: { tableKey: string; applied: number; deleted: number }) => (
                  <tr key={entry.tableKey}>
                    <th>{entry.tableKey}</th>
                    <td>
                      applied <b>{entry.applied}</b> · deleted <b>{entry.deleted}</b>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
          <a
            href={`/sp3/devtools/userview/${result.destinationUserId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary">
            <i className="fas fa-external-link-alt mr-2" />
            Check on User View
          </a>
          </>
        )}
      </Card.Body>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning mr-2" />
            Overwrite QA account?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will <b>overwrite</b> the destination AppUser fields and
            replace all with the file&apos;s data.
          </p>
          {parsedPreview && (
            <p className="mb-0">
              From file user <code>{parsedPreview.sourceUserId}</code> → to
              <code> {destinationUserId.trim()}</code>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm}>
            Overwrite
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ImportPanel;
